import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Box, Grid, Typography } from "@mui/material";
import React, { memo } from "react";
import MapaOrigem, { COR_DA_DISTRIBUIDORA, COR_DO_PRODUTOR } from "../MapaOrigem";
import styles from "../../Rastreabilidade.module.css";

const fontDescriStyle = {
	fontWeight: "500",
	fontSize: "20px",
};

/** Chave do mapa: repete a cor e a letra que aparecem dentro do pino. */
const LegendaDoMapa = ({ cor, letra, texto }) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
		<Box
			sx={{
				width: 20,
				height: 20,
				borderRadius: "50%",
				bgcolor: cor,
				color: "#fff",
				fontSize: 12,
				fontWeight: 700,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			{letra}
		</Box>
		{texto}
	</Box>
);

/** Uns cadastros gravam o CEP com máscara, outros só os dígitos. */
const formatarCep = (cep) => {
	const digitos = (cep ?? "").replace(/\D/g, "");
	if (digitos.length !== 8) return cep || null;
	return `${digitos.slice(0, 5)}-${digitos.slice(5)}`;
};

/**
 * Endereço em uma linha só, pulando o que o cadastro não preencheu:
 * "Rua X, 123 — Centro, Canindé - CE, 62700-000".
 */
const enderecoCompleto = (local) => {
	if (!local) return null;
	const ruaEBairro = [local.logradouro, local.bairro].filter(Boolean).join(" — ");
	const cidadeUf = [local.cidade, local.uf].filter(Boolean).join(" - ");
	return (
		[ruaEBairro, cidadeUf, formatarCep(local.cep)].filter(Boolean).join(", ") ||
		null
	);
};

const formatDate = (valor) =>
	valor ? new Date(valor).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "-";

const descricaoDaEmbalagem = (embalagem) => {
	if (!embalagem) return null;
	const medida = [embalagem.quantidade, embalagem.unidadeMedida]
		.filter(Boolean)
		.join(" ");
	return medida ? `${embalagem.descricao} (${medida})` : embalagem.descricao;
};

/**
 * Espelha o TraceabilityProdut do campo: dados à esquerda, mapa da origem à
 * direita. Quando não há nenhuma coordenada cadastrada, a coluna do mapa cai
 * para a galeria da distribuidora em vez de ficar vazia.
 */
function RastreioProduto({ dados }) {
	const produto = dados?.produto;
	const producao = dados?.producao;
	const origem = dados?.origem;
	const distribuidora = dados?.distribuidora;
	const galeria = distribuidora?.galeria ?? [];
	const embalagem = descricaoDaEmbalagem(produto?.embalagem);
	const produtores = origem?.produtores ?? [];

	const temMapa =
		produtores.some((p) => p.latitude != null || p.poligono?.length >= 3) ||
		(distribuidora?.latitude != null && distribuidora?.longitude != null);

	return (
		<Box component="main" sx={{ width: "100%", m: 0 }}>
			<Grid
				container
				sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
			>
				<Grid
					size={{ xs: 12, md: 6 }}
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						py: 5,
						px: { xs: 3, sm: 8 },
						minHeight: { md: "100vh" },
					}}
				>
					<Typography
						variant="h3"
						className={styles.titleTraceabilityProdut}
						sx={{ fontWeight: "700", color: "#009199", mb: 3 }}
					>
						Rastreabilidade do Produto
					</Typography>

					<Box sx={{ mb: "25px" }}>
						<Typography variant="h5" sx={{ fontWeight: "700" }}>
							{produto?.nome ?? "Produto não informado"}
							{origem?.qualidade ? ` (${origem.qualidade})` : ""}
						</Typography>

						{embalagem ? (
							<Typography variant="h6" sx={{ fontWeight: "500" }}>
								{embalagem}
							</Typography>
						) : (
							produto?.volumes > 0 && (
								<Typography variant="h6" sx={{ fontWeight: "500" }}>
									{produto.volumes} volume(s)
								</Typography>
							)
						)}

						<Typography variant="h6" sx={{ fontWeight: "500" }}>
							Data de produção: {formatDate(producao?.data)}
						</Typography>
						<Typography variant="h6" sx={{ fontWeight: "500" }}>
							Data de expedição: {formatDate(dados?.venda?.dataExpedicao)}
						</Typography>

						<Typography variant="h5" sx={{ fontWeight: "700", mt: 2 }}>
							Código de Rastreabilidade:
						</Typography>
						<Typography variant="h6" sx={{ fontWeight: "500" }}>
							{dados?.codigo}
						</Typography>
					</Box>

					{origem?.produtores?.length > 0 && (
						<Box sx={{ mb: "25px" }}>
							<Typography
								variant="h6"
								sx={{ fontWeight: "500", display: "flex", alignItems: "center" }}
							>
								<LocationOnIcon sx={{ color: "red" }} />
								<b>Origem:</b>
							</Typography>
							{origem.produtores.map((produtor, indice) => (
								<Box key={indice} sx={{ mb: 1 }}>
									<Typography variant="body1" sx={fontDescriStyle}>
										{produtor.nome}
										{produtor.propriedade ? ` — ${produtor.propriedade}` : ""}
									</Typography>
									{enderecoCompleto(produtor) && (
										<Typography variant="body1" sx={fontDescriStyle}>
											{enderecoCompleto(produtor)}
										</Typography>
									)}
								</Box>
							))}
						</Box>
					)}

					<Box sx={{ mb: "25px" }}>
						<Typography
							variant="h6"
							sx={{ fontWeight: "500", display: "flex", alignItems: "center" }}
						>
							<LocationOnIcon sx={{ color: "red" }} />
							<b>Distribuído por:</b>
						</Typography>
						<Typography variant="body1" sx={fontDescriStyle}>
							{distribuidora?.nome}
						</Typography>
						{enderecoCompleto(distribuidora) && (
							<Typography variant="body1" sx={fontDescriStyle}>
								{enderecoCompleto(distribuidora)}
							</Typography>
						)}
					</Box>
				</Grid>

				<Grid
					size={{ xs: 12, md: 6 }}
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						px: { xs: 3, sm: 6 },
						py: 5,
					}}
				>
					{temMapa ? (
						<Box sx={{ width: "100%" }}>
							<MapaOrigem produtores={produtores} distribuidora={distribuidora} />
							<Box
								sx={{
									display: "flex",
									gap: 3,
									justifyContent: "center",
									mt: 1.5,
									fontSize: 14,
									color: "#444",
								}}
							>
								<LegendaDoMapa cor={COR_DO_PRODUTOR} letra="P" texto="Produtor" />
								<LegendaDoMapa cor={COR_DA_DISTRIBUIDORA} letra="D" texto="Distribuidora" />
							</Box>
						</Box>
					) : (
						<Grid container rowSpacing={2} columnSpacing={2} sx={{ width: "100%" }}>
							{galeria.length > 0
								? galeria.slice(0, 4).map((imagem, indice) => (
									<Grid size={{ xs: 12, sm: galeria.length > 1 ? 6 : 12 }} key={indice}>
										<img
											src={imagem}
											className={styles.imgGaleria}
											alt={`Foto ${indice + 1} da distribuidora`}
										/>
									</Grid>
								))
								: distribuidora?.foto && (
									<Grid size={12}>
										<img
											src={distribuidora.foto}
											className={styles.imgGaleria}
											alt={distribuidora?.nome ?? ""}
										/>
									</Grid>
								)}
						</Grid>
					)}
				</Grid>
			</Grid>
		</Box>
	);
}

export default memo(RastreioProduto);
